<script setup>
import RiskService from '@/services/quality/risk.service'
import { RiskStatus } from '@/enums/quality/risk-status'
import RiskDetailHeader from '@/views/quality/risk/sections/RiskDetailHeader.vue'
import RiskInfoSection from '@/views/quality/risk/sections/RiskInfoSection.vue'
import RiskScoreSection from '@/views/quality/risk/sections/RiskScoreSection.vue'
import RiskTreatmentSection from '@/views/quality/risk/sections/RiskTreatmentSection.vue'
import RiskReviewSection from '@/views/quality/risk/sections/RiskReviewSection.vue'
import RiskScoreHistorySection from '@/views/quality/risk/sections/RiskScoreHistorySection.vue'
import RiskCreateDrawer from '@/views/quality/risk/RiskCreateDrawer.vue'
import RiskScoreDrawer from '@/views/quality/risk/RiskScoreDrawer.vue'
import RiskReviewDrawer from '@/views/quality/risk/RiskReviewDrawer.vue'
import RiskTreatmentDrawer from '@/views/quality/risk/RiskTreatmentDrawer.vue'
import RiskLifecycleDialog from '@/views/quality/risk/RiskLifecycleDialog.vue'
import RiskInvestigateDrawer from '@/views/quality/risk/RiskInvestigateDrawer.vue'
import RiskInvestigationSection from '@/views/quality/risk/sections/RiskInvestigationSection.vue'
import RiskClosureSection from '@/views/quality/risk/sections/RiskClosureSection.vue'
import { useRiskMatrixBands } from '@/composables/quality/useRiskMatrixBands'

// VSkeletonLoader lives in Vuetify labs and is NOT auto-registered by this
// project's component resolver — an unimported labs component renders nothing
// at all, with no console error, so the whole loading state was invisible.
// See holiday-calender/index.vue for the same import.
import { VSkeletonLoader } from 'vuetify/lib/labs/components.mjs'

// unplugin-vue-router derives the route name from the file path
// (`app-quality-risk-id`), not from meta — meta.name is not read by the
// router. The permission guard only checks meta.subject/meta.action (see
// src/plugins/2.router/guards.js:94), so, matching the sibling
// src/pages/app/quality/infection-control/exposure/[id].vue convention, no
// `name` key is set here.
definePage({
  meta: {
    subject: 'risk.menu',
    action: 'register',
  },
})

const route = useRoute()
const router = useRouter()

const risk = ref(null)
const isLoading = ref(false)

// Monotonic token guarding against out-of-order responses — see
// src/pages/app/quality/risk/index.vue for the same pattern. The detail page
// re-fetches after every drawer save, so an earlier in-flight response must
// not clobber a later one.
let fetchToken = 0

const fetchRisk = async () => {
  const token = ++fetchToken

  isLoading.value = true

  // useApi's top-level onError never fires (wrong key name for useFetch), so a
  // failed GET produces no toast either — the error callback below is the only
  // signal. Leaving risk null while isLoading goes false is what the template's
  // final v-else branch renders: without it, v-if="risk" and
  // v-else-if="isLoading" were both false and a deleted risk, a cross-unit id
  // or a stale bookmark rendered a blank white page.
  await RiskService.show(route.params.id, data => {
    if (token !== fetchToken) return
    risk.value = data?.data ?? data ?? null
    isLoading.value = false
  }, () => {
    if (token !== fetchToken) return
    risk.value = null
    isLoading.value = false
  })
}

fetchRisk()

const isEditDrawerVisible = ref(false)
const isScoreDrawerVisible = ref(false)
const isReviewDrawerVisible = ref(false)
const isTreatmentDrawerVisible = ref(false)
const isLifecycleDialogVisible = ref(false)
const isInvestigateDrawerVisible = ref(false)

const treatmentDrawerTarget = ref(null)
const lifecycleMode = ref('accept')

const isClosed = computed(() => risk.value?.status === RiskStatus.CLOSED)
const isAccepted = computed(() => risk.value?.status === RiskStatus.ACCEPTED)

// A closed risk is a settled record: the redesign removes every mutating
// control rather than leaving buttons that write to a terminal record.
const isReadOnly = isClosed

const { bands, maxScore } = useRiskMatrixBands(
  computed(() => risk.value?.unit_id),
  computed(() => risk.value?.scoring_method),
)

// Bumped whenever a new RCA is started so RiskInvestigationSection remounts
// and re-fetches — it does not expose a re-fetch method of its own.
const investigationsTableKey = ref(0)

const onSaved = () => {
  fetchRisk()
}

const onInvestigationCreated = () => {
  investigationsTableKey.value++
}

const openTreatmentDrawer = (treatment = null) => {
  treatmentDrawerTarget.value = treatment
  isTreatmentDrawerVisible.value = true
}

const openLifecycleDialog = mode => {
  lifecycleMode.value = mode
  isLifecycleDialogVisible.value = true
}
</script>

<template>
  <section
    v-if="risk"
    class="risk-detail-page"
  >
    <RiskDetailHeader
      :risk="risk"
      :is-read-only="isReadOnly"
      @back="router.push({ name: 'app-quality-risk' })"
      @edit="isEditDrawerVisible = true"
      @score="isScoreDrawerVisible = true"
      @review="isReviewDrawerVisible = true"
      @investigate="isInvestigateDrawerVisible = true"
      @lifecycle="openLifecycleDialog"
    />

    <div class="d-flex flex-column gap-6">
      <RiskClosureSection
        v-if="isClosed || isAccepted"
        :risk="risk"
      />

      <RiskScoreSection
        :risk="risk"
        :bands="bands"
        :max-score="maxScore"
        :is-read-only="isReadOnly"
        @score="isScoreDrawerVisible = true"
        @add-treatment="openTreatmentDrawer(null)"
      />

      <RiskInfoSection :risk="risk" />

      <RiskTreatmentSection
        :risk="risk"
        :is-read-only="isReadOnly"
        @add="openTreatmentDrawer(null)"
        @edit="openTreatmentDrawer($event)"
        @saved="onSaved"
      />

      <RiskReviewSection
        :risk="risk"
        :is-read-only="isReadOnly"
        @review="isReviewDrawerVisible = true"
      />
      <RiskScoreHistorySection
        :risk="risk"
        :bands="bands"
        :max-score="maxScore"
      />

      <RiskInvestigationSection
        :key="investigationsTableKey"
        :risk="risk"
        :is-read-only="isReadOnly"
        @investigate="isInvestigateDrawerVisible = true"
      />
    </div>

    <RiskCreateDrawer
      v-model:is-drawer-open="isEditDrawerVisible"
      :risk="risk"
      @saved="onSaved"
    />

    <RiskScoreDrawer
      v-model:is-drawer-open="isScoreDrawerVisible"
      :risk="risk"
      @saved="onSaved"
    />

    <RiskReviewDrawer
      v-model:is-drawer-open="isReviewDrawerVisible"
      :risk="risk"
      @saved="onSaved"
    />

    <RiskTreatmentDrawer
      v-model:is-drawer-open="isTreatmentDrawerVisible"
      :risk="risk"
      :treatment="treatmentDrawerTarget"
      @saved="onSaved"
    />

    <RiskLifecycleDialog
      v-model:is-drawer-open="isLifecycleDialogVisible"
      :risk="risk"
      :mode="lifecycleMode"
      @saved="onSaved"
    />

    <RiskInvestigateDrawer
      v-model:is-drawer-open="isInvestigateDrawerVisible"
      :risk="risk"
      @created="onInvestigationCreated"
    />
  </section>

  <!--
    Loading state.

    One standard skeleton type per region rather than hand-composed bones. The
    header used to stack three separate VSkeletonLoaders (avatar + two text
    lines) inside a flex row, which rendered badly: every VSkeletonLoader is its
    own root with its own padding, so the two stacked `text` bones got uneven
    gaps, and `width` sizes the loader ROOT, not the avatar bone inside it — so
    the circle never matched the 48px square back button it stood in for.

    `list-item-avatar-two-line` expands to `avatar, sentences`, which is exactly
    the shape of the real header: a control, a title and a subtitle.
  -->
  <section v-else-if="isLoading">
    <VSkeletonLoader
      type="list-item-avatar-two-line"
      class="mb-6"
    />
    <VSkeletonLoader type="card" />
  </section>

  <!--
    Error state — a failed or empty fetch must never render a blank page.
    The class carries this page's sentence-case button rule.
  -->
  <section
    v-else
    class="risk-detail-page"
  >
    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="text-center py-12">
        <VIcon
          icon="tabler-alert-triangle"
          size="48"
          color="warning"
          class="mb-4"
        />
        <h6 class="text-h6 mb-2">
          This risk could not be loaded
        </h6>
        <p class="text-body-2 text-medium-emphasis mb-6">
          It may have been deleted, or it belongs to a unit you do not have access to.
        </p>
        <div class="d-flex justify-center gap-3">
          <VBtn
            variant="tonal"
            @click="fetchRisk"
          >
            Try again
          </VBtn>
          <VBtn
            color="primary"
            :to="{ name: 'app-quality-risk' }"
          >
            Back to register
          </VBtn>
        </div>
      </VCardText>
    </VCard>
  </section>
</template>

<style lang="scss" scoped>
// The reference darkens warning-toned TEXT to #C77420 in light theme and only
// falls back to the warning token in dark (reference:81-82, 197-198, 284-285,
// 347-348) — the raw token is washed out on a 2.9rem numeral. Declared once
// here; CSS custom properties inherit, so every descendant section reads it
// without importing anything.
.risk-detail-page {
  --risk-warn: #c77420;
  --risk-inherent: #e64a19;
}

.v-theme--dark .risk-detail-page {
  --risk-warn: rgb(var(--v-theme-warning));
}

// The app's Vuetify button typography sets text-transform: capitalize
// (@core/scss/template/libs/vuetify/_variables.scss:161), which renders the
// mockup's "Record review" as "Record Review". The redesign follows the
// mockup's sentence case. Scoped to this page — :deep() is required because
// the buttons live inside child section components, and the global rule is
// deliberately left alone so the rest of the app is unaffected. Drawers
// (Edit/Score/etc.) mount inside this section rather than teleporting, so
// their buttons are excluded to keep the app-wide Title Case there.
.risk-detail-page :deep(.v-btn:not(.v-navigation-drawer .v-btn)) {
  text-transform: none;
}

// The app's global VChip default sets label: true (plugins/vuetify/defaults.js),
// which squares the corners. The mockup's chips are rounded pills. Scoped to
// this page so the rest of the app is unaffected, and excluded from drawers for
// the same reason as the button rule — VNavigationDrawer mounts INSIDE this
// page's subtree, so an unqualified :deep() would leak into every drawer.
.risk-detail-page :deep(.v-chip:not(.v-navigation-drawer .v-chip)) {
  border-radius: 999px;
}
</style>
